<template>
  <div class="edit-widget-columns">
    <!-- Main Menu Items -->
    <div 
      v-for="(item, index) in settings" 
      :key="item.getMenuInfo().name" 
      class="item"
      :class="{ 
        'active': current === index,
        'prev': index === (current - 1), 
        'next': index === (current + 1)
      }" 
      @click.stop="handleItemClick(item, index)"
    >
      <div class="icon-wrapper">
        <im-icon :icon="item.getMenuInfo().icon" class="menu-icon" />
      </div>
      <span class="name">{{ item.getMenuInfo().name }}</span>
      
      <!-- Active Indicator (Glowing Bar) -->
      <div class="active-bar" v-if="current === index"></div>
    </div>

    <!-- Bottom Settings -->
    <div class="bottom-group">
      <div class="item setting-item" @click.stop="handleAppSettingClick">
        <div class="icon-wrapper">
          <IconSetting class="menu-icon" />
        </div>
        <span class="name">{{ SiteColumnsNameEnum.SETTING }}</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, getCurrentInstance } from 'vue'
import IconSetting from '@/assets/svg/icon/iconSetting.svg'
import { SiteColumnsNameEnum } from "~/enums/siteEnum"

const props = defineProps({
  settings: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['show-app-setting'])
const { proxy } = getCurrentInstance()
const store = proxy.$store

const current = ref(0)

// Methods
const closeMenus = () => {
  proxy.SiteMenu.close()
  store.commit('user/CLEAR_SITE_LOGIN_USER_MODAL')
}

const handleItemClick = (item, index) => {
  if (proxy.SiteMenu.columnIndex === index) return
  closeMenus()
  proxy.SiteMenu.setColumnIndex(index)
  proxy.SiteMenu.setColumnChildIndex(null)
  current.value = index
}

const handleAppSettingClick = () => {
  emit('show-app-setting')
}

const setCurrent = (index) => {
  current.value = index
}

// Expose methods for parent component usage
defineExpose({
  setCurrent,
  handleItemClick
})
</script>

<style lang="less" scoped>
// Design Variables
@sidebar-width: @edit-columns-w;
@sidebar-bg: rgba(18, 18, 23, 0.95);
@sidebar-border: 1px solid rgba(255, 255, 255, 0.08);
@item-height: 84px;
@primary-glow: rgba(var(--primary-color-rgb), 0.6);
@text-color: rgba(255, 255, 255, 0.45);
@text-active: #ffffff;
@hover-bg: rgba(255, 255, 255, 0.04);

.edit-widget-columns {
  width: @sidebar-width;
  height: calc(100vh - 56px);
  position: relative;
  flex-shrink: 0;
  background: @sidebar-bg;
  backdrop-filter: blur(24px);
  border-right: @sidebar-border;
  display: flex;
  flex-direction: column;
  z-index: 100;
  user-select: none;
  padding-top: 8px;
  box-shadow: 1px 0 20px rgba(0, 0, 0, 0.2);

  .item {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    height: @item-height;
    cursor: pointer;
    color: @text-color;
    transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
    margin: 4px 8px;
    border-radius: 12px;

    .icon-wrapper {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;
      border-radius: 10px;
      transition: all 0.3s ease;
      background: transparent;
      
      .menu-icon {
        font-size: 22px;
        transition: all 0.3s ease;
        filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
        fill: currentColor;
      }
    }

    .name {
      margin-top: 6px;
      font-size: 11px;
      font-weight: 500;
      letter-spacing: 0.5px;
      opacity: 0.8;
      transition: all 0.3s ease;
    }

    // Hover State
    &:hover {
      background: @hover-bg;
      color: rgba(255, 255, 255, 0.8);

      .icon-wrapper {
        transform: translateY(-2px);
        
        .menu-icon {
          transform: scale(1.05);
          filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.4));
        }
      }

      .name {
        opacity: 1;
      }
    }

    // Active State
    &.active {
      background: linear-gradient(135deg, rgba(var(--primary-color-rgb), 0.15) 0%, rgba(var(--primary-color-rgb), 0.05) 100%);
      color: @text-active;

      .icon-wrapper {
        background: linear-gradient(135deg, rgba(var(--primary-color-rgb), 0.3) 0%, rgba(var(--primary-color-rgb), 0.1) 100%);
        box-shadow: 0 4px 12px rgba(var(--primary-color-rgb), 0.2);
        transform: translateY(-2px);

        .menu-icon {
          color: #fff;
          filter: drop-shadow(0 0 8px @primary-glow);
          transform: scale(1.1);
        }
      }

      .name {
        font-weight: 600;
        opacity: 1;
        text-shadow: 0 0 10px rgba(var(--primary-color-rgb), 0.5);
      }
    }

    // Active Indicator Bar (Left side)
    .active-bar {
      position: absolute;
      left: -8px; 
      top: 50%;
      transform: translateY(-50%);
      width: 3px;
      height: 24px;
      background: var(--primary-color);
      border-radius: 0 4px 4px 0;
      box-shadow: 0 0 10px @primary-glow;
      animation: glow 1.5s infinite alternate;
    }
  }

  .bottom-group {
    margin-top: auto;
    width: 100%;
    padding: 16px 8px;
    border-top: 1px solid rgba(255, 255, 255, 0.04);
    background: linear-gradient(to top, rgba(0,0,0,0.2), transparent);

    .setting-item {
      height: 72px;
      margin: 0;
      
      &:hover {
        background: rgba(255, 255, 255, 0.06);
        
        .icon-wrapper {
          transform: rotate(30deg);
        }
      }
    }
  }
}

@keyframes glow {
  from {
    box-shadow: 0 0 5px @primary-glow;
    opacity: 0.8;
  }
  to {
    box-shadow: 0 0 15px @primary-glow;
    opacity: 1;
  }
}
</style>